/**
 * ICS (iCalendar) and Google Calendar Parser Utility
 * Parses .ics files and Google Calendar events
 */

/**
 * Parse an ICS file content and extract events
 * @param {string} icsContent - The content of the .ics file
 * @returns {Array} Array of parsed events
 */
export const parseICS = (icsContent) => {
  if (!icsContent || typeof icsContent !== 'string') {
    throw new Error('Invalid ICS content provided');
  }

  // Validate ICS content
  if (!icsContent.includes('BEGIN:VCALENDAR') || !icsContent.includes('END:VCALENDAR')) {
    throw new Error('Invalid ICS file format - missing VCALENDAR wrapper');
  }

  const events = [];
  const lines = icsContent.split(/\r?\n/);
  let currentEvent = null;
  let inEvent = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Handle line folding (lines starting with space or tab)
    if (line.startsWith(' ') || line.startsWith('\t')) {
      if (currentEvent && inEvent) {
        // Append to previous line
        const lastKey = Object.keys(currentEvent).pop();
        if (lastKey && currentEvent[lastKey]) {
          currentEvent[lastKey] += line.substring(1);
        }
      }
      continue;
    }

    // Start of a new event
    if (line === 'BEGIN:VEVENT') {
      currentEvent = {};
      inEvent = true;
      continue;
    }

    // End of current event
    if (line === 'END:VEVENT') {
      if (currentEvent && inEvent) {
        events.push(parseEvent(currentEvent));
        currentEvent = null;
        inEvent = false;
      }
      continue;
    }

    // Parse event properties
    if (inEvent && currentEvent && line.includes(':')) {
      const colonIndex = line.indexOf(':');
      const key = line.substring(0, colonIndex).toUpperCase();
      const value = line.substring(colonIndex + 1);
      
      currentEvent[key] = value;
    }
  }

  return events;
};

/**
 * Parse a single event object
 * @param {Object} eventData - Raw event data from ICS
 * @returns {Object} Parsed event object
 */
const parseEvent = (eventData) => {
  const event = {
    name: '',
    description: '',
    location: '',
    start_datetime: null,
    end_datetime: null,
    category: 'other',
    google_map_link: ''
  };

  // Parse summary (event name)
  if (eventData.SUMMARY) {
    event.name = eventData.SUMMARY;
  }

  // Parse description
  if (eventData.DESCRIPTION) {
    event.description = eventData.DESCRIPTION;
  }

  // Parse location
  if (eventData.LOCATION) {
    event.location = eventData.LOCATION;
  }

  // Parse start time
  if (eventData.DTSTART) {
    event.start_datetime = parseDateTime(eventData.DTSTART);
  }

  // Parse end time
  if (eventData.DTEND) {
    event.end_datetime = parseDateTime(eventData.DTEND);
  } else if (eventData.DURATION && event.start_datetime) {
    // Calculate end time from duration
    const duration = parseDuration(eventData.DURATION);
    if (duration) {
      event.end_datetime = new Date(event.start_datetime.getTime() + duration);
    }
  }

  // Determine category based on event name, description, and location
  event.category = determineCategory(event.name, event.description, event.location);

  return event;
};

/**
 * Parse ICS datetime format - supports multiple formats with timezone handling
 * @param {string} dateTimeString - ICS datetime string
 * @returns {Date} Parsed date object
 */
const parseDateTime = (dateTimeString) => {
  if (!dateTimeString) return null;

  // Try different parsing methods
  let parsedDate = null;

  // Method 1: Standard ICS format (YYYYMMDDTHHMMSSZ or YYYYMMDDTHHMMSS)
  if (dateTimeString.match(/^\d{8}T\d{6}Z?$/)) {
    const isUTC = dateTimeString.endsWith('Z');
    const cleanDateTime = dateTimeString.replace(/[TZ]/g, '');
    
    if (cleanDateTime.length >= 8) {
      const year = parseInt(cleanDateTime.substring(0, 4));
      const month = parseInt(cleanDateTime.substring(4, 6)) - 1; // Month is 0-indexed
      const day = parseInt(cleanDateTime.substring(6, 8));
      
      let hour = 0;
      let minute = 0;
      let second = 0;
      
      if (cleanDateTime.length >= 10) {
        hour = parseInt(cleanDateTime.substring(8, 10)) || 0;
      }
      if (cleanDateTime.length >= 12) {
        minute = parseInt(cleanDateTime.substring(10, 12)) || 0;
      }
      if (cleanDateTime.length >= 14) {
        second = parseInt(cleanDateTime.substring(12, 14)) || 0;
      }
      
      if (isUTC) {
        // Create UTC date
        parsedDate = new Date(Date.UTC(year, month, day, hour, minute, second));
      } else {
        // Create local date
        parsedDate = new Date(year, month, day, hour, minute, second);
      }
    }
  }
  
  // Method 2: Date only format (YYYYMMDD)
  else if (dateTimeString.match(/^\d{8}$/)) {
    const year = parseInt(dateTimeString.substring(0, 4));
    const month = parseInt(dateTimeString.substring(4, 6)) - 1;
    const day = parseInt(dateTimeString.substring(6, 8));
    parsedDate = new Date(year, month, day);
  }
  
  // Method 3: ISO 8601 format with timezone (YYYY-MM-DDTHH:MM:SS+HH:MM or YYYY-MM-DDTHH:MM:SSZ)
  else if (dateTimeString.includes('-') && dateTimeString.includes('T')) {
    try {
      parsedDate = new Date(dateTimeString);
    } catch (e) {
      console.warn('Failed to parse ISO 8601 date:', dateTimeString);
    }
  }
  
  // Method 4: RFC 2822 format (common in some calendar exports)
  else if (dateTimeString.includes(', ')) {
    try {
      parsedDate = new Date(dateTimeString);
    } catch (e) {
      console.warn('Failed to parse RFC 2822 date:', dateTimeString);
    }
  }
  
  // Method 5: ICS floating date format (YYYYMMDDTHHMMSS without timezone)
  else if (dateTimeString.match(/^\d{8}T\d{6}$/)) {
    const cleanDateTime = dateTimeString.replace('T', '');
    if (cleanDateTime.length >= 8) {
      const year = parseInt(cleanDateTime.substring(0, 4));
      const month = parseInt(cleanDateTime.substring(4, 6)) - 1;
      const day = parseInt(cleanDateTime.substring(6, 8));
      
      let hour = 0;
      let minute = 0;
      let second = 0;
      
      if (cleanDateTime.length >= 10) {
        hour = parseInt(cleanDateTime.substring(8, 10)) || 0;
      }
      if (cleanDateTime.length >= 12) {
        minute = parseInt(cleanDateTime.substring(10, 12)) || 0;
      }
      if (cleanDateTime.length >= 14) {
        second = parseInt(cleanDateTime.substring(12, 14)) || 0;
      }
      
      // Floating date (no timezone info)
      parsedDate = new Date(year, month, day, hour, minute, second);
    }
  }
  
  // Method 6: Try native Date parsing as fallback
  else {
    try {
      parsedDate = new Date(dateTimeString);
      // Check if the date is valid
      if (isNaN(parsedDate.getTime())) {
        parsedDate = null;
      }
    } catch (e) {
      console.warn('Failed to parse date:', dateTimeString);
    }
  }

  return parsedDate;
};

/**
 * Parse ICS duration format (PT1H30M format)
 * @param {string} durationString - ICS duration string
 * @returns {number} Duration in milliseconds
 */
const parseDuration = (durationString) => {
  if (!durationString || !durationString.startsWith('PT')) {
    return null;
  }
  
  const duration = durationString.substring(2); // Remove 'PT' prefix
  let totalMs = 0;
  
  // Parse hours
  const hoursMatch = duration.match(/(\d+)H/);
  if (hoursMatch) {
    totalMs += parseInt(hoursMatch[1]) * 60 * 60 * 1000;
  }
  
  // Parse minutes
  const minutesMatch = duration.match(/(\d+)M/);
  if (minutesMatch) {
    totalMs += parseInt(minutesMatch[1]) * 60 * 1000;
  }
  
  // Parse seconds
  const secondsMatch = duration.match(/(\d+)S/);
  if (secondsMatch) {
    totalMs += parseInt(secondsMatch[1]) * 1000;
  }
  
  return totalMs;
};


/**
 * Validate if events are within trip date range
 * @param {Array} events - Array of parsed events
 * @param {string} tripStartDate - Trip start date (YYYY-MM-DD)
 * @param {string} tripEndDate - Trip end date (YYYY-MM-DD)
 * @returns {Object} Validation result with valid events and warnings
 */
export const validateEventsForTrip = (events, tripStartDate, tripEndDate) => {
  const tripStart = new Date(tripStartDate);
  const tripEnd = new Date(tripEndDate);
  tripEnd.setHours(23, 59, 59, 999); // Include the entire end date
  
  const validEvents = [];
  const warnings = [];
  
  events.forEach((event, index) => {
    if (!event.start_datetime) {
      warnings.push(`Event ${index + 1} (${event.name || 'Untitled'}) has no start time and will be skipped`);
      return;
    }
    
    const eventDate = new Date(event.start_datetime);
    eventDate.setHours(0, 0, 0, 0); // Compare dates only
    
    if (eventDate >= tripStart && eventDate <= tripEnd) {
      validEvents.push(event);
    } else {
      warnings.push(`Event "${event.name || 'Untitled'}" is outside trip date range (${tripStartDate} to ${tripEndDate})`);
    }
  });
  
  return {
    validEvents,
    warnings,
    totalEvents: events.length,
    validCount: validEvents.length
  };
};

/**
 * Convert parsed events to itinerary format
 * @param {Array} events - Array of parsed events
 * @param {number} tripId - Trip ID
 * @param {number} userId - User ID
 * @returns {Array} Array of itinerary items ready for API
 */
export const convertEventsToItinerary = (events, tripId, userId) => {
  return events.map(event => ({
    name: event.name || 'Imported Event',
    location: event.location || '',
    description: event.description || '',
    category: event.category,
    start_datetime: event.start_datetime ? event.start_datetime.toISOString().replace('Z', '') : null,
    end_datetime: event.end_datetime ? event.end_datetime.toISOString().replace('Z', '') : null,
    google_map_link: event.google_map_link || ''
  }));
};

/**
 * Check if a file is a valid ICS file
 * @param {File} file - File object
 * @returns {boolean} True if file appears to be an ICS file
 */
export const isICSFile = (file) => {
  if (!file) return false;
  
  // Check file extension
  const fileName = file.name.toLowerCase();
  if (fileName.endsWith('.ics') || 
      fileName.endsWith('.ical') || 
      fileName.endsWith('.ifb') || 
      fileName.endsWith('.icalendar')) {
    return true;
  }
  
  // Check MIME type
  const mimeType = file.type.toLowerCase();
  if (mimeType === 'text/calendar' || 
      mimeType === 'application/calendar' ||
      mimeType === 'text/x-vcalendar' ||
      mimeType === 'application/ics') {
    return true;
  }
  
  return false;
};

/**
 * Read ICS file content
 * @param {File} file - ICS file
 * @returns {Promise<string>} File content
 */
export const readICSFile = (file) => {
  return new Promise((resolve, reject) => {
    if (!isICSFile(file)) {
      reject(new Error('File is not a valid ICS file'));
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve(e.target.result);
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsText(file);
  });
};

/**
 * Parse Google Calendar events
 * @param {Array} googleEvents - Array of Google Calendar event objects
 * @returns {Array} Array of parsed events
 */
export const parseGoogleCalendarEvents = (googleEvents) => {
  if (!Array.isArray(googleEvents)) {
    throw new Error('Invalid Google Calendar events provided');
  }

  return googleEvents.map(event => parseGoogleEvent(event));
};

/**
 * Parse a single Google Calendar event
 * @param {Object} googleEvent - Google Calendar event object
 * @returns {Object} Parsed event object
 */
const parseGoogleEvent = (googleEvent) => {
  const event = {
    name: '',
    description: '',
    location: '',
    start_datetime: null,
    end_datetime: null,
    category: 'other',
    google_map_link: ''
  };

  // Parse event name
  if (googleEvent.summary) {
    event.name = googleEvent.summary;
  }

  // Parse description
  if (googleEvent.description) {
    event.description = googleEvent.description;
  }

  // Parse location
  if (googleEvent.location) {
    event.location = googleEvent.location;
  }

  // Parse start time
  if (googleEvent.start) {
    if (googleEvent.start.dateTime) {
      event.start_datetime = parseGoogleDateTime(googleEvent.start.dateTime);
    } else if (googleEvent.start.date) {
      // All-day event
      event.start_datetime = parseGoogleDateTime(googleEvent.start.date);
    }
  }

  // Parse end time
  if (googleEvent.end) {
    if (googleEvent.end.dateTime) {
      event.end_datetime = parseGoogleDateTime(googleEvent.end.dateTime);
    } else if (googleEvent.end.date) {
      // All-day event
      event.end_datetime = parseGoogleDateTime(googleEvent.end.date);
    }
  }

  // Determine category based on event name, description, and location
  event.category = determineCategory(event.name, event.description, event.location);

  return event;
};

/**
 * Parse Google Calendar datetime - supports multiple formats with timezone handling
 * @param {string} dateTimeString - Google Calendar datetime string
 * @returns {Date} Parsed date object
 */
const parseGoogleDateTime = (dateTimeString) => {
  if (!dateTimeString) return null;

  try {
    // Google Calendar provides various formats:
    // 1. ISO 8601 with timezone: 2024-11-20T21:00:00+00:00
    // 2. ISO 8601 UTC: 2024-11-20T21:00:00Z
    // 3. ISO 8601 local: 2024-11-20T21:00:00
    // 4. Date only: 2024-11-20 (for all-day events)
    
    let parsedDate = null;
    
    // Handle date-only format (all-day events)
    if (dateTimeString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // All-day event - set to start of day in local timezone
      const [year, month, day] = dateTimeString.split('-').map(Number);
      parsedDate = new Date(year, month - 1, day);
    } else {
      // Try parsing as ISO 8601
      parsedDate = new Date(dateTimeString);
    }
    
    // Check if the date is valid
    if (isNaN(parsedDate.getTime())) {
      console.warn('Invalid Google Calendar date:', dateTimeString);
      return null;
    }
    
    return parsedDate;
  } catch (error) {
    console.warn('Failed to parse Google Calendar date:', dateTimeString, error);
    return null;
  }
};

/**
 * Enhanced category determination with location context
 * @param {string} name - Event name
 * @param {string} description - Event description
 * @param {string} location - Event location
 * @returns {string} Category
 */
const determineCategory = (name, description, location = '') => {
  const text = `${name} ${description} ${location}`.toLowerCase();
  
  if (text.includes('hotel') || text.includes('accommodation') || text.includes('check-in') || text.includes('check-out') || text.includes('resort')) {
    return 'hotel';
  }
  
  if (text.includes('restaurant') || text.includes('dining') || text.includes('lunch') || text.includes('dinner') || text.includes('breakfast') || text.includes('cafe') || text.includes('bar')) {
    return 'restaurant';
  }
  
  if (text.includes('flight') || text.includes('airport') || text.includes('departure') || text.includes('arrival') || text.includes('transport') || text.includes('bus') || text.includes('train') || text.includes('taxi')) {
    return 'transport';
  }
  
  if (text.includes('tour') || text.includes('activity') || text.includes('excursion') || text.includes('sightseeing') || text.includes('museum') || text.includes('park') || text.includes('beach')) {
    return 'activity';
  }
  
  if (text.includes('meeting') || text.includes('conference') || text.includes('event') || text.includes('seminar') || text.includes('workshop') || text.includes('presentation')) {
    return 'event';
  }
  
  return 'other';
};

/**
 * Check if Google Calendar API is available
 * @returns {boolean} True if Google Calendar API is loaded
 */
export const isGoogleCalendarAvailable = () => {
  return !!(window.google && window.google.calendar && window.google.calendar.events);
};

/**
 * Load Google Calendar events for a specific date range
 * @param {string} calendarId - Google Calendar ID (default: 'primary')
 * @param {Date} startDate - Start date for events
 * @param {Date} endDate - End date for events
 * @returns {Promise<Array>} Array of Google Calendar events
 */
export const loadGoogleCalendarEvents = async (calendarId = 'primary', startDate, endDate) => {
  if (!isGoogleCalendarAvailable()) {
    throw new Error('Google Calendar API not available');
  }

  try {
    const response = await window.google.calendar.events.list({
      calendarId: calendarId,
      timeMin: startDate.toISOString(),
      timeMax: endDate.toISOString(),
      singleEvents: true,
      orderBy: 'startTime'
    });

    return response.result.items || [];
  } catch (error) {
    console.error('Error loading Google Calendar events:', error);
    throw new Error('Failed to load Google Calendar events');
  }
};

/**
 * Check if user is authenticated with Google Calendar
 * @returns {Promise<boolean>} True if authenticated
 */
export const isGoogleCalendarAuthenticated = async () => {
  if (!window.google || !window.google.auth) {
    return false;
  }

  try {
    const authInstance = window.google.auth.getAuthInstance();
    return authInstance.isSignedIn.get();
  } catch (error) {
    console.error('Error checking Google Calendar authentication:', error);
    return false;
  }
};

/**
 * Authenticate with Google Calendar
 * @param {string} clientId - Google OAuth client ID
 * @param {Array} scopes - Required scopes
 * @returns {Promise<boolean>} True if authentication successful
 */
export const authenticateGoogleCalendar = async (clientId, scopes = ['https://www.googleapis.com/auth/calendar.readonly']) => {
  if (!window.google || !window.google.auth) {
    throw new Error('Google API not loaded');
  }

  try {
    const authInstance = window.google.auth.getAuthInstance();
    
    if (!authInstance.isSignedIn.get()) {
      await authInstance.signIn({
        scope: scopes.join(' ')
      });
    }
    
    return authInstance.isSignedIn.get();
  } catch (error) {
    console.error('Error authenticating with Google Calendar:', error);
    throw new Error('Failed to authenticate with Google Calendar');
  }
};

/**
 * Get user's Google Calendar list
 * @returns {Promise<Array>} Array of calendar objects
 */
export const getGoogleCalendarList = async () => {
  if (!isGoogleCalendarAvailable()) {
    throw new Error('Google Calendar API not available');
  }

  try {
    const response = await window.google.calendar.calendarList.list();
    return response.result.items || [];
  } catch (error) {
    console.error('Error loading Google Calendar list:', error);
    throw new Error('Failed to load Google Calendar list');
  }
};
